// AgentFormModal.tsx — add or edit an agent record. Admin-only: it is only
// opened from the admin Agents page, and the API refuses anyone else.

import React, { useEffect, useState } from 'react';
import { Check, Pencil, UserPlus, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import ProposalDialog, { Notice } from '../../Proposals/parts/ProposalDialog';
import { apiError } from '../../Proposals/parts/proposalTypes';
import { AGENT_LIMITS, Agent, AgentBody, agentApi } from './agentTypes';

interface AgentFormModalProps {
  visible: boolean;
  /** null → add, an agent → edit */
  agent: Agent | null;
  onHide: () => void;
  onSaved: (a: Agent) => void;
}

const AgentFormModal: React.FC<AgentFormModalProps> = ({ visible, agent, onHide, onSaved }) => {
  const isEdit = !!agent;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [city, setCity] = useState('');
  const [profession, setProfession] = useState('');
  const [address, setAddress] = useState('');
  const [commission, setCommission] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setName(agent?.name ?? '');
    setPhone(agent?.phone ?? '');
    setEmail(agent?.email ?? '');
    setCompany(agent?.company ?? '');
    setCity(agent?.city ?? '');
    setProfession(agent?.profession ?? '');
    setAddress(agent?.address ?? '');
    setCommission(agent?.commission != null ? String(agent.commission) : '');
    setNotes(agent?.notes ?? '');
    setError(null);
  }, [visible, agent]);

  const submit = async () => {
    const n = name.trim();
    const p = phone.trim();
    const e = email.trim();
    const co = company.trim();
    const ci = city.trim();
    const pr = profession.trim();
    const ad = address.trim();
    const nt = notes.trim();
    const cm = commission.trim();

    if (!n) return setError('Give the agent a name.');
    if (!p) return setError('Add a phone number.');
    if (p.replace(/\D/g, '').length < 7) return setError('Enter a valid phone number.');
    if (e && !/^\S+@\S+\.\S+$/.test(e)) return setError('Enter a valid email address.');
    let pct: number | null = null;
    if (cm) {
      pct = Number(cm);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) return setError('Commission must be a number between 0 and 100.');
    }

    setSaving(true);
    setError(null);
    try {
      if (isEdit && agent) {
        // send only what changed
        const body: AgentBody = {};
        if (n !== agent.name) body.name = n;
        if (p !== agent.phone) body.phone = p;
        if (e !== (agent.email ?? '')) body.email = e;
        if (co !== (agent.company ?? '')) body.company = co;
        if (ci !== (agent.city ?? '')) body.city = ci;
        if (pr !== (agent.profession ?? '')) body.profession = pr;
        if (ad !== (agent.address ?? '')) body.address = ad;
        if (nt !== (agent.notes ?? '')) body.notes = nt;
        if (pct !== (agent.commission ?? null)) body.commission = pct;
        if (Object.keys(body).length === 0) {
          onHide();
          return;
        }
        const res = await agentApi.update(agent.id, body);
        onSaved(res.data);
      } else {
        const res = await agentApi.create({ name: n, phone: p, email: e || undefined, company: co || undefined, city: ci || undefined, profession: pr || undefined, address: ad || undefined, commission: pct ?? undefined, notes: nt || undefined });
        onSaved(res.data);
      }
      onHide();
    } catch (err) {
      setError(apiError(err, isEdit ? 'Could not update the agent.' : 'Could not add the agent.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProposalDialog
      visible={visible}
      onHide={onHide}
      title={isEdit ? 'Edit agent' : 'New agent'}
      subtitle={isEdit ? 'Update this agent’s record' : 'Keep a record of someone who brings you leads'}
      icon={isEdit ? Pencil : UserPlus}
      busy={saving}
      maxWidth="520px"
      footer={
        <>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onHide} disabled={saving} />
          <GlobalButton label={isEdit ? 'SAVE CHANGES' : 'ADD AGENT'} icon={Check} onClick={submit} loading={saving} />
        </>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}

      <GlobalInputField label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rohit Sharma" maxLength={AGENT_LIMITS.name} disabled={saving} inputProps={{ autoFocus: true }} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
        <GlobalInputField label="Phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" type="tel" disabled={saving} hint="Each agent needs a different number." />
        <GlobalInputField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="optional" type="email" maxLength={AGENT_LIMITS.email} disabled={saving} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
        <GlobalInputField label="Profession" value={profession} onChange={(e) => setProfession(e.target.value)} placeholder="e.g. Property dealer, Teacher" maxLength={AGENT_LIMITS.profession} disabled={saving} />
        <GlobalInputField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="optional" maxLength={AGENT_LIMITS.company} disabled={saving} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
        <GlobalInputField label="City" value={city} onChange={(e) => setCity(e.target.value)} placeholder="optional" maxLength={AGENT_LIMITS.city} disabled={saving} />
        <GlobalInputField label="Commission %" value={commission} onChange={(e) => setCommission(e.target.value)} placeholder="optional" type="number" disabled={saving} />
      </div>

      <GlobalTextArea label="Address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, area, pincode (optional)" rows={2} maxLength={AGENT_LIMITS.address} disabled={saving} />

      <GlobalTextArea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering about this agent (optional)" rows={3} maxLength={AGENT_LIMITS.notes} disabled={saving} />
    </ProposalDialog>
  );
};

export default AgentFormModal;